import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  Check,
  ChefHat,
  ImagePlus,
  Plus,
  Save,
  Sparkles,
  Trash2,
} from "lucide-react";
import LoadingOverlay from "../../components/Loading/LoadingOverlay";
import CustomAlert from "../../components/Alerts/CustomAlert";
import MultiPicker from "../Workshops/MultiPicker";
import { addAlert, removeAlert } from "../../Utils/helper";
import {
  createRecipe,
  getApiError,
  getRecipe,
  listIngredientMaster,
  listRecipeAudiences,
  listRecipeCategories,
  listRecipeExperts,
  listRecipeTags,
  listRecipeTypes,
  updateRecipe,
  uploadRecipeMedia,
} from "../../Utils/recipeAdminApi";
import RecipeSectionHelp from "./RecipeSectionHelp";
import RecipeToggle from "./RecipeToggle";
import "./RecipesAdmin.css";

const STEPS = [
  { key: "basics", label: "Recipe details", note: "Title, story, media" },
  {
    key: "classification",
    label: "Classification",
    note: "Chef, diet, discovery",
  },
  {
    key: "ingredients",
    label: "Ingredients",
    note: "Master items and amounts",
  },
  { key: "directions", label: "Directions", note: "Ordered cooking steps" },
  {
    key: "nutrition",
    label: "Nutrition & publish",
    note: "Nutrition and visibility",
  },
];

const STEP_HELP = [
  {
    title: "How recipe details work",
    text: "This section controls the recipe's main identity and the content shown on recipe cards and detail pages.",
    items: [
      "Use a clear, unique title and a short description that makes sense in search results.",
      "The slug becomes the recipe URL; leave it blank for a safe automatic value.",
      "About supports rich text. The banner is uploaded after the recipe record is saved.",
      "Card and hero images are optimized variants for discovery cards and wide detail headers; banner remains the legacy fallback.",
      "Making video should be a complete, publicly reachable URL.",
    ],
  },
  {
    title: "How classification works",
    text: "Classification decides where the recipe appears and which filters can discover it in the app.",
    items: [
      "Assign a chef when the recipe should be attributed to an expert.",
      "Categories group recipes; audiences describe who the recipe is for; recipe types describe the meal or format.",
      "Difficulty, diet, and preparation time power user-facing labels and filters.",
      "Serving values, age range, family-safe approval, and tags drive the current personalised mobile experience.",
      "Manage missing categories or master data from Recipe setup before returning here.",
    ],
  },
  {
    title: "How ingredients work",
    text: "Each row links this recipe to one reusable item in the ingredient master.",
    items: [
      "Choose the ingredient first, then enter a human-readable quantity such as 2 cups or ½ tsp.",
      "Use the preparation note for instructions such as finely chopped, softened, or divided.",
      "Structured quantity, unit, position, and scalable status let the app adjust amounts when servings change.",
      "Create missing ingredients in the ingredient master instead of typing duplicate names.",
      "Removing a row only removes it from this recipe; it does not delete the master ingredient.",
    ],
  },
  {
    title: "How directions work",
    text: "Directions form the ordered cooking flow users follow on the recipe page.",
    items: [
      "Keep one clear action or cooking stage per step.",
      "The heading is optional; the rich-text instruction is required.",
      "Add a duration and timer label when the app should offer an in-step cooking timer.",
      "Use the arrow controls to reorder steps. Step numbers are recalculated automatically.",
      "Removing a step permanently removes it when the recipe is saved.",
    ],
  },
  {
    title: "How nutrition and publishing work",
    text: "This final section controls nutrition, internal attribution, catalogue order, and visibility.",
    items: [
      "Nutrition values are per serving; include units for protein, fat, and carbs.",
      "Structured grams and daily-value percentages power the current nutrition display while legacy text remains available for older clients.",
      "Display order controls manual ordering where the app uses the recipe index.",
      "View count is the catalogue total; change it only when correcting imported data.",
      "Save draft keeps the recipe hidden. Publish makes it eligible to appear in the app immediately.",
    ],
  },
];

const StepHelp = ({ index }) => {
  const help = STEP_HELP[index];
  return (
    <RecipeSectionHelp title={help.title}>
      <p>{help.text}</p>
      <ul>
        {help.items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </RecipeSectionHelp>
  );
};

const blankRecipe = () => ({
  title: "",
  slug: "",
  description: "",
  about: "",
  preparation_time: "",
  difficulty_level: "Easy",
  category_ids: [],
  intended_audience_ids: [],
  type_ids: [],
  tag_ids: [],
  making_video: "",
  chef_id: null,
  user_id: "",
  view_count: 0,
  is_ai_generated: false,
  is_active: false,
  diet_type: "veg",
  servings: 1,
  serving_label: "serving",
  prep_minutes: "",
  cook_minutes: "",
  minimum_age_months: "",
  maximum_age_months: "",
  is_family_safe: false,
  rating: "",
  review_count: 0,
  index: 0,
  ingredients: [],
  directions: [],
  nutrition: {
    kCal: 0,
    protein: "",
    fat: "",
    carbs: "",
    serving_size: "",
    calories_kcal: "",
    protein_g: "",
    fat_g: "",
    carbs_g: "",
    fiber_g: "",
    iron_dv_percent: "",
    vitamin_a_dv_percent: "",
  },
});

const isRichTextEmpty = (value) =>
  !String(value || "")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();

const Field = ({ label, required, error, hint, children, wide = false }) => (
  <label className={`recipe-field ${wide ? "is-wide" : ""}`}>
    <span className="recipe-field-label">
      {label} {required && <em>*</em>}
    </span>
    {children}
    {hint && !error && <small>{hint}</small>}
    {error && <small className="recipe-field-error">{error}</small>}
  </label>
);

const RecipeForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(blankRecipe);
  const [mediaFiles, setMediaFiles] = useState({});
  const [mediaPreviews, setMediaPreviews] = useState({});
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [refs, setRefs] = useState({
    categories: [],
    ingredients: [],
    audiences: [],
    types: [],
    experts: [],
    tags: [],
  });

  const notify = (title, message, type) =>
    addAlert(title, message, type, true, setAlerts);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      try {
        const [
          categories,
          ingredients,
          audiences,
          types,
          experts,
          tags,
          recipe,
        ] =
          await Promise.all([
            listRecipeCategories({ page_size: 200 }),
            listIngredientMaster(),
            listRecipeAudiences(),
            listRecipeTypes(),
            listRecipeExperts(),
            listRecipeTags({ page_size: 200 }),
            isEdit ? getRecipe(id) : Promise.resolve(null),
          ]);
        if (cancelled) return;
        setRefs({
          categories,
          ingredients,
          audiences,
          types,
          experts: experts.map((expert) => ({
            ...expert,
            title: expert.name || expert.title,
          })),
          tags: tags.map((tag) => ({ ...tag, title: tag.label })),
        });
        if (recipe) {
          setForm({
            ...blankRecipe(),
            ...recipe,
            preparation_time: recipe.preparation_time?.slice(0, 5) || "",
            chef_id: recipe.chef_id || null,
            user_id: recipe.user_id || "",
            category_ids: recipe.category_ids || [],
            intended_audience_ids: recipe.intended_audience_ids || [],
            type_ids: recipe.type_ids || [],
            tag_ids: recipe.tag_ids || [],
            ingredients: recipe.ingredients || [],
            directions: recipe.directions || [],
            nutrition: recipe.nutrition?.[0] || blankRecipe().nutrition,
          });
          setMediaPreviews({
            banner: recipe.banner || "",
            card_image: recipe.card_image || "",
            hero_image: recipe.hero_image || "",
          });
        }
      } catch (error) {
        notify("Could not open recipe", getApiError(error), "error");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  const set = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const ingredientOptions = useMemo(
    () => refs.ingredients.map((item) => ({ id: item.id, title: item.title })),
    [refs.ingredients]
  );

  const validateStep = (stepIndex) => {
    const nextErrors = {};
    if (stepIndex === 0) {
      if (!form.title.trim()) nextErrors.title = "Recipe title is required.";
      if (!form.description.trim())
        nextErrors.description = "Add a short catalogue description.";
      if (form.making_video) {
        try {
          // eslint-disable-next-line no-new
          new URL(form.making_video);
        } catch {
          nextErrors.making_video = "Enter a complete video URL.";
        }
      }
    }
    if (stepIndex === 1) {
      if (!form.difficulty_level) {
        nextErrors.difficulty_level = "Choose a difficulty.";
      }
      if (
        form.minimum_age_months !== "" &&
        form.maximum_age_months !== "" &&
        Number(form.maximum_age_months) < Number(form.minimum_age_months)
      ) {
        nextErrors.maximum_age_months =
          "Maximum age must be at least the minimum age.";
      }
    }
    if (stepIndex === 2) {
      form.ingredients.forEach((item, index) => {
        if (!item.ingredients_id) {
          nextErrors[`ingredient_${index}`] = "Choose an ingredient.";
        }
      });
    }
    if (stepIndex === 3) {
      form.directions.forEach((item, index) => {
        if (isRichTextEmpty(item.Directions)) {
          nextErrors[`direction_${index}`] = "Cooking instruction is required.";
        }
      });
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const jumpTo = (index) => {
    if (index <= step || validateStep(step)) setStep(index);
  };

  const updateIngredient = (index, key, value) => {
    const items = [...form.ingredients];
    items[index] = { ...items[index], [key]: value };
    set("ingredients", items);
  };

  const addIngredient = () =>
    set("ingredients", [
      ...form.ingredients,
      {
        ingredients_id: null,
        quantity: "",
        help_text: "",
        position: form.ingredients.length,
        quantity_value: "",
        quantity_unit: "",
        is_scalable: true,
      },
    ]);

  const updateDirection = (index, key, value) => {
    const items = [...form.directions];
    items[index] = { ...items[index], [key]: value };
    set("directions", items);
  };

  const addDirection = () =>
    set("directions", [
      ...form.directions,
      {
        step: form.directions.length + 1,
        head_name: "",
        Directions: "",
        duration_seconds: "",
        timer_label: "",
      },
    ]);

  const moveDirection = (index, offset) => {
    const target = index + offset;
    if (target < 0 || target >= form.directions.length) return;
    const items = [...form.directions];
    [items[index], items[target]] = [items[target], items[index]];
    set(
      "directions",
      items.map((item, itemIndex) => ({ ...item, step: itemIndex + 1 }))
    );
  };

  const buildPayload = (publish) => ({
    title: form.title.trim(),
    ...(form.slug.trim() ? { slug: form.slug.trim() } : {}),
    description: form.description.trim(),
    about: form.about || "",
    preparation_time: form.preparation_time || null,
    difficulty_level: form.difficulty_level,
    category_ids: form.category_ids,
    intended_audience_ids: form.intended_audience_ids,
    type_ids: form.type_ids,
    tag_ids: form.tag_ids,
    making_video: form.making_video || "",
    chef_id: form.chef_id || null,
    user_id: form.user_id === "" ? null : Number(form.user_id),
    view_count: Number(form.view_count || 0),
    is_ai_generated: Boolean(form.is_ai_generated),
    is_active: publish,
    diet_type: form.diet_type,
    servings: Number(form.servings || 1),
    serving_label: form.serving_label || "",
    prep_minutes:
      form.prep_minutes === "" ? null : Number(form.prep_minutes),
    cook_minutes:
      form.cook_minutes === "" ? null : Number(form.cook_minutes),
    minimum_age_months:
      form.minimum_age_months === ""
        ? null
        : Number(form.minimum_age_months),
    maximum_age_months:
      form.maximum_age_months === ""
        ? null
        : Number(form.maximum_age_months),
    is_family_safe: Boolean(form.is_family_safe),
    rating: form.rating === "" ? null : Number(form.rating),
    review_count: Number(form.review_count || 0),
    index: Number(form.index || 0),
    ingredients: form.ingredients.map((item, index) => ({
      ingredients_id: Number(item.ingredients_id),
      quantity: item.quantity || "",
      help_text: item.help_text || "",
      position: index,
      quantity_value:
        item.quantity_value === "" || item.quantity_value === null
          ? null
          : Number(item.quantity_value),
      quantity_unit: item.quantity_unit || "",
      is_scalable: Boolean(item.is_scalable),
    })),
    directions: form.directions.map((item, index) => ({
      step: index + 1,
      head_name: item.head_name || "",
      Directions: item.Directions || "",
      duration_seconds:
        item.duration_seconds === "" || item.duration_seconds === null
          ? null
          : Number(item.duration_seconds),
      timer_label: item.timer_label || "",
    })),
    nutrition: {
      kCal: Number(form.nutrition.kCal || 0),
      protein: form.nutrition.protein || "",
      fat: form.nutrition.fat || "",
      carbs: form.nutrition.carbs || "",
      serving_size: form.nutrition.serving_size || "",
      calories_kcal:
        form.nutrition.calories_kcal === ""
          ? null
          : Number(form.nutrition.calories_kcal),
      protein_g:
        form.nutrition.protein_g === ""
          ? null
          : Number(form.nutrition.protein_g),
      fat_g:
        form.nutrition.fat_g === ""
          ? null
          : Number(form.nutrition.fat_g),
      carbs_g:
        form.nutrition.carbs_g === ""
          ? null
          : Number(form.nutrition.carbs_g),
      fiber_g:
        form.nutrition.fiber_g === ""
          ? null
          : Number(form.nutrition.fiber_g),
      iron_dv_percent:
        form.nutrition.iron_dv_percent === ""
          ? null
          : Number(form.nutrition.iron_dv_percent),
      vitamin_a_dv_percent:
        form.nutrition.vitamin_a_dv_percent === ""
          ? null
          : Number(form.nutrition.vitamin_a_dv_percent),
    },
  });

  const save = async (publish) => {
    for (let index = 0; index < STEPS.length; index += 1) {
      if (!validateStep(index)) {
        setStep(index);
        notify(
          "Review the highlighted fields",
          "The recipe is not ready to save.",
          "error"
        );
        return;
      }
    }
    setIsSaving(true);
    try {
      const saved = isEdit
        ? await updateRecipe(id, buildPayload(publish))
        : await createRecipe(buildPayload(publish));
      if (saved?.id) {
        await Promise.all(
          Object.entries(mediaFiles).map(([field, file]) =>
            uploadRecipeMedia(saved.id, field, file)
          )
        );
      }
      notify(
        publish ? "Recipe published" : "Draft saved",
        saved?.title || form.title,
        "success"
      );
      window.setTimeout(() => navigate("/recipes"), 450);
    } catch (error) {
      notify("Recipe not saved", getApiError(error), "error");
    } finally {
      setIsSaving(false);
    }
  };

  const nutritionSet = (field, value) =>
    set("nutrition", { ...form.nutrition, [field]: value });

  return (
    <div className="recipe-admin-page">
      {(isLoading || isSaving) && <LoadingOverlay />}

      <header className="recipe-form-header">
        <button
          className="recipe-icon-back"
          onClick={() => navigate("/recipes")}
        >
          <ArrowLeft size={19} />
        </button>
        <div>
          <span className="recipe-eyebrow">
            <ChefHat size={15} /> {isEdit ? `Recipe #${id}` : "New recipe"}
          </span>
          <h1>{isEdit ? "Edit recipe" : "Create a recipe"}</h1>
          <p>
            Build the complete app experience, from ingredients to publishing.
          </p>
        </div>
        <div className="recipe-form-header-actions">
          <button
            className="recipe-button recipe-button-secondary"
            disabled={isSaving}
            onClick={() => save(false)}
          >
            <Save size={17} /> Save draft
          </button>
          <button
            className="recipe-button recipe-button-primary"
            disabled={isSaving}
            onClick={() => save(true)}
          >
            <Sparkles size={17} /> Publish
          </button>
        </div>
      </header>

      <div className="recipe-form-layout">
        <aside className="recipe-stepper">
          {STEPS.map((item, index) => (
            <button
              key={item.key}
              className={`${index === step ? "is-active" : ""} ${
                index < step ? "is-complete" : ""
              }`}
              onClick={() => jumpTo(index)}
            >
              <span>{index < step ? <Check size={15} /> : index + 1}</span>
              <div>
                <strong>{item.label}</strong>
                <small>{item.note}</small>
              </div>
            </button>
          ))}
        </aside>

        <main className="recipe-panel recipe-form-panel">
          {step === 0 && (
            <section className="recipe-form-section">
              <div className="recipe-section-heading">
                <span>01</span>
                <div>
                  <h2>Tell the recipe story</h2>
                  <p>The content users see before they begin cooking.</p>
                </div>
              </div>
              <StepHelp index={0} />
              <div className="recipe-form-grid">
                <Field label="Recipe title" required error={errors.title}>
                  <input
                    value={form.title}
                    onChange={(e) => set("title", e.target.value)}
                    placeholder="e.g. Creamy spinach pasta"
                  />
                </Field>
                <Field
                  label="Slug"
                  hint="Leave blank to generate it automatically."
                >
                  <input
                    value={form.slug}
                    onChange={(e) => set("slug", e.target.value)}
                    placeholder="creamy-spinach-pasta"
                  />
                </Field>
                <Field
                  label="Short description"
                  required
                  error={errors.description}
                  wide
                >
                  <textarea
                    value={form.description}
                    onChange={(e) => set("description", e.target.value)}
                    placeholder="A concise description for recipe cards and search."
                  />
                </Field>
                <Field label="About this recipe" wide>
                  <div className="recipe-rich-editor">
                    <ReactQuill
                      theme="snow"
                      value={form.about || ""}
                      onChange={(value) => set("about", value)}
                    />
                  </div>
                </Field>
                <Field
                  label="Making video URL"
                  error={errors.making_video}
                  wide
                >
                  <input
                    type="url"
                    value={form.making_video}
                    onChange={(e) => set("making_video", e.target.value)}
                    placeholder="https://youtube.com/watch?v=…"
                  />
                </Field>
                {[
                  {
                    key: "banner",
                    label: "Legacy banner",
                    hint: "Fallback image used by older recipe clients.",
                  },
                  {
                    key: "card_image",
                    label: "Card image",
                    hint: "Optimized landscape image for discovery cards.",
                  },
                  {
                    key: "hero_image",
                    label: "Hero image",
                    hint: "Wide image for the recipe detail header.",
                  },
                ].map((media) => (
                  <Field key={media.key} label={media.label} hint={media.hint} wide>
                    <div className="recipe-banner-picker">
                      <div className="recipe-banner-preview">
                        {mediaPreviews[media.key] ? (
                          <img src={mediaPreviews[media.key]} alt={`${media.label} preview`} />
                        ) : (
                          <span>
                            <ImagePlus size={27} /> No image selected
                          </span>
                        )}
                      </div>
                      <label className="recipe-file-button">
                        <ImagePlus size={16} /> Choose image
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(event) => {
                            const selected = event.target.files?.[0];
                            if (!selected) return;
                            setMediaFiles((current) => ({
                              ...current,
                              [media.key]: selected,
                            }));
                            setMediaPreviews((current) => ({
                              ...current,
                              [media.key]: URL.createObjectURL(selected),
                            }));
                          }}
                        />
                      </label>
                    </div>
                  </Field>
                ))}
              </div>
            </section>
          )}

          {step === 1 && (
            <section className="recipe-form-section">
              <div className="recipe-section-heading">
                <span>02</span>
                <div>
                  <h2>Organise discovery</h2>
                  <p>
                    Control how the recipe is grouped, filtered, and attributed.
                  </p>
                </div>
              </div>
              <StepHelp index={1} />
              <div className="recipe-form-grid">
                <Field label="Chef">
                  <MultiPicker
                    single
                    options={refs.experts}
                    value={form.chef_id}
                    onChange={(value) => set("chef_id", value)}
                    placeholder="Search chefs…"
                  />
                </Field>
                <Field label="Preparation time">
                  <input
                    type="time"
                    value={form.preparation_time}
                    onChange={(e) => set("preparation_time", e.target.value)}
                  />
                </Field>
                <Field label="Prep minutes">
                  <input
                    type="number"
                    min="0"
                    value={form.prep_minutes}
                    onChange={(e) => set("prep_minutes", e.target.value)}
                  />
                </Field>
                <Field label="Cook minutes">
                  <input
                    type="number"
                    min="0"
                    value={form.cook_minutes}
                    onChange={(e) => set("cook_minutes", e.target.value)}
                  />
                </Field>
                <Field label="Servings">
                  <input
                    type="number"
                    min="1"
                    value={form.servings}
                    onChange={(e) => set("servings", e.target.value)}
                  />
                </Field>
                <Field label="Serving label">
                  <input
                    value={form.serving_label}
                    onChange={(e) => set("serving_label", e.target.value)}
                    placeholder="e.g. bowl, portion"
                  />
                </Field>
                <Field
                  label="Difficulty"
                  required
                  error={errors.difficulty_level}
                >
                  <select
                    value={form.difficulty_level}
                    onChange={(e) => set("difficulty_level", e.target.value)}
                  >
                    <option value="Easy">Easy</option>
                    <option value="Medium">Medium</option>
                    <option value="Hard">Hard</option>
                  </select>
                </Field>
                <Field label="Diet type">
                  <select
                    value={form.diet_type}
                    onChange={(e) => set("diet_type", e.target.value)}
                  >
                    <option value="veg">Vegetarian</option>
                    <option value="nonveg">Non-vegetarian</option>
                    <option value="contains_egg">Contains egg</option>
                  </select>
                </Field>
                <Field label="Categories" wide>
                  <MultiPicker
                    options={refs.categories}
                    value={form.category_ids}
                    onChange={(value) => set("category_ids", value)}
                    placeholder="Add categories…"
                  />
                </Field>
                <Field label="Intended audience" wide>
                  <MultiPicker
                    options={refs.audiences}
                    value={form.intended_audience_ids}
                    onChange={(value) => set("intended_audience_ids", value)}
                    placeholder="Add audiences…"
                  />
                </Field>
                <Field label="Recipe types" wide>
                  <MultiPicker
                    options={refs.types}
                    value={form.type_ids}
                    onChange={(value) => set("type_ids", value)}
                    placeholder="Add recipe types…"
                  />
                </Field>
                <Field label="Tags" wide>
                  <MultiPicker
                    options={refs.tags}
                    value={form.tag_ids}
                    onChange={(value) => set("tag_ids", value)}
                    placeholder="Add feature, goal, allergen, or dietary tags…"
                  />
                </Field>
                <Field label="Minimum age (months)">
                  <input
                    type="number"
                    min="0"
                    value={form.minimum_age_months}
                    onChange={(e) => set("minimum_age_months", e.target.value)}
                  />
                </Field>
                <Field
                  label="Maximum age (months)"
                  error={errors.maximum_age_months}
                >
                  <input
                    type="number"
                    min="0"
                    value={form.maximum_age_months}
                    onChange={(e) => set("maximum_age_months", e.target.value)}
                  />
                </Field>
                <RecipeToggle
                  id="recipe-family-safe"
                  className="recipe-toggle-row is-wide"
                  checked={form.is_family_safe}
                  label="Family-safe approval"
                  onChange={(checked) => set("is_family_safe", checked)}
                >
                  <span>
                    <strong>Family-safe approval</strong>
                    <small>
                      Explicit content-team approval when no child age range is configured.
                    </small>
                  </span>
                </RecipeToggle>
              </div>
            </section>
          )}

          {step === 2 && (
            <section className="recipe-form-section">
              <div className="recipe-section-heading recipe-section-heading-with-action">
                <span>03</span>
                <div>
                  <h2>Build the ingredient list</h2>
                  <p>
                    Choose from the shared ingredient master and add quantities.
                  </p>
                </div>
                <button
                  className="recipe-button recipe-button-secondary"
                  onClick={addIngredient}
                >
                  <Plus size={16} /> Add ingredient
                </button>
              </div>
              <StepHelp index={2} />
              <div className="recipe-builder-list">
                {form.ingredients.length === 0 && (
                  <div className="recipe-builder-empty">
                    <ChefHat size={24} />
                    <strong>No ingredients yet</strong>
                    <p>Add the first ingredient for this recipe.</p>
                  </div>
                )}
                {form.ingredients.map((item, index) => (
                  <article
                    className="recipe-builder-card ingredient-card"
                    key={`${item.id || "new"}-${index}`}
                  >
                    <span className="recipe-builder-number">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <div className="recipe-builder-fields">
                      <Field
                        label="Ingredient"
                        required
                        error={errors[`ingredient_${index}`]}
                      >
                        <MultiPicker
                          single
                          options={ingredientOptions}
                          value={item.ingredients_id}
                          onChange={(value) =>
                            updateIngredient(index, "ingredients_id", value)
                          }
                          placeholder="Search ingredient master…"
                        />
                      </Field>
                      <Field label="Quantity">
                        <input
                          value={item.quantity || ""}
                          onChange={(e) =>
                            updateIngredient(index, "quantity", e.target.value)
                          }
                          placeholder="e.g. 2 cups"
                        />
                      </Field>
                      <Field label="Preparation note">
                        <input
                          value={item.help_text || ""}
                          onChange={(e) =>
                            updateIngredient(index, "help_text", e.target.value)
                          }
                          placeholder="e.g. finely chopped"
                        />
                      </Field>
                      <Field label="Quantity value">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.quantity_value ?? ""}
                          onChange={(e) =>
                            updateIngredient(index, "quantity_value", e.target.value)
                          }
                          placeholder="e.g. 2"
                        />
                      </Field>
                      <Field label="Quantity unit">
                        <input
                          value={item.quantity_unit || ""}
                          onChange={(e) =>
                            updateIngredient(index, "quantity_unit", e.target.value)
                          }
                          placeholder="e.g. cup"
                        />
                      </Field>
                      <RecipeToggle
                        id={`recipe-ingredient-scalable-${index}`}
                        className="recipe-toggle-row"
                        checked={item.is_scalable ?? true}
                        label="Scales with servings"
                        onChange={(checked) =>
                          updateIngredient(index, "is_scalable", checked)
                        }
                      >
                        <span>
                          <strong>Scales with servings</strong>
                          <small>Adjust this amount when the serving count changes.</small>
                        </span>
                      </RecipeToggle>
                    </div>
                    <button
                      className="recipe-remove-row"
                      aria-label="Remove ingredient"
                      onClick={() =>
                        set(
                          "ingredients",
                          form.ingredients.filter(
                            (_, itemIndex) => itemIndex !== index
                          )
                        )
                      }
                    >
                      <Trash2 size={16} />
                    </button>
                  </article>
                ))}
              </div>
              <button
                className="recipe-inline-link"
                onClick={() => navigate("/recipes/setup?tab=ingredients")}
              >
                Missing an item? Manage the ingredient master →
              </button>
            </section>
          )}

          {step === 3 && (
            <section className="recipe-form-section">
              <div className="recipe-section-heading recipe-section-heading-with-action">
                <span>04</span>
                <div>
                  <h2>Write the directions</h2>
                  <p>
                    Create clear steps in the exact order users should follow.
                  </p>
                </div>
                <button
                  className="recipe-button recipe-button-secondary"
                  onClick={addDirection}
                >
                  <Plus size={16} /> Add step
                </button>
              </div>
              <StepHelp index={3} />
              <div className="recipe-builder-list">
                {form.directions.length === 0 && (
                  <div className="recipe-builder-empty">
                    <BookOpenIcon />
                    <strong>No directions yet</strong>
                    <p>Add a step to begin the cooking flow.</p>
                  </div>
                )}
                {form.directions.map((item, index) => (
                  <article
                    className="recipe-builder-card direction-card"
                    key={`${item.id || "new"}-${index}`}
                  >
                    <div className="recipe-direction-rail">
                      <span>STEP {index + 1}</span>
                      <button
                        disabled={index === 0}
                        onClick={() => moveDirection(index, -1)}
                        aria-label="Move up"
                      >
                        <ArrowUp size={15} />
                      </button>
                      <button
                        disabled={index === form.directions.length - 1}
                        onClick={() => moveDirection(index, 1)}
                        aria-label="Move down"
                      >
                        <ArrowDown size={15} />
                      </button>
                    </div>
                    <div className="recipe-direction-content">
                      <Field label="Step heading">
                        <input
                          value={item.head_name || ""}
                          onChange={(e) =>
                            updateDirection(index, "head_name", e.target.value)
                          }
                          placeholder="e.g. Prepare the sauce"
                        />
                      </Field>
                      <div className="recipe-form-grid">
                        <Field label="Timer duration (seconds)">
                          <input
                            type="number"
                            min="0"
                            value={item.duration_seconds ?? ""}
                            onChange={(e) =>
                              updateDirection(index, "duration_seconds", e.target.value)
                            }
                          />
                        </Field>
                        <Field label="Timer label">
                          <input
                            value={item.timer_label || ""}
                            onChange={(e) =>
                              updateDirection(index, "timer_label", e.target.value)
                            }
                            placeholder="e.g. Simmer sauce"
                          />
                        </Field>
                      </div>
                      <Field
                        label="Instructions"
                        required
                        error={errors[`direction_${index}`]}
                      >
                        <div className="recipe-rich-editor compact">
                          <ReactQuill
                            theme="snow"
                            value={item.Directions || ""}
                            onChange={(value) =>
                              updateDirection(index, "Directions", value)
                            }
                          />
                        </div>
                      </Field>
                    </div>
                    <button
                      className="recipe-remove-row"
                      aria-label="Remove direction"
                      onClick={() =>
                        set(
                          "directions",
                          form.directions
                            .filter((_, itemIndex) => itemIndex !== index)
                            .map((direction, itemIndex) => ({
                              ...direction,
                              step: itemIndex + 1,
                            }))
                        )
                      }
                    >
                      <Trash2 size={16} />
                    </button>
                  </article>
                ))}
              </div>
            </section>
          )}

          {step === 4 && (
            <section className="recipe-form-section">
              <div className="recipe-section-heading">
                <span>05</span>
                <div>
                  <h2>Nutrition and publishing</h2>
                  <p>
                    Finish the nutritional profile and control catalogue
                    visibility.
                  </p>
                </div>
              </div>
              <StepHelp index={4} />
              <div className="recipe-nutrition-block">
                <h3>Nutrition per serving</h3>
                <div className="recipe-form-grid four-columns">
                  <Field label="Serving size">
                    <input
                      value={form.nutrition.serving_size || ""}
                      onChange={(e) => nutritionSet("serving_size", e.target.value)}
                      placeholder="e.g. 1 bowl (250g)"
                    />
                  </Field>
                  <Field label="Calories (kcal)">
                    <input
                      type="number"
                      min="0"
                      value={form.nutrition.calories_kcal ?? ""}
                      onChange={(e) => nutritionSet("calories_kcal", e.target.value)}
                    />
                  </Field>
                  <Field label="Protein (g)">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.nutrition.protein_g ?? ""}
                      onChange={(e) => nutritionSet("protein_g", e.target.value)}
                    />
                  </Field>
                  <Field label="Fat (g)">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.nutrition.fat_g ?? ""}
                      onChange={(e) => nutritionSet("fat_g", e.target.value)}
                    />
                  </Field>
                  <Field label="Carbs (g)">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.nutrition.carbs_g ?? ""}
                      onChange={(e) => nutritionSet("carbs_g", e.target.value)}
                    />
                  </Field>
                  <Field label="Fiber (g)">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.nutrition.fiber_g ?? ""}
                      onChange={(e) => nutritionSet("fiber_g", e.target.value)}
                    />
                  </Field>
                  <Field label="Iron daily value (%)">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.nutrition.iron_dv_percent ?? ""}
                      onChange={(e) => nutritionSet("iron_dv_percent", e.target.value)}
                    />
                  </Field>
                  <Field label="Vitamin A daily value (%)">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.nutrition.vitamin_a_dv_percent ?? ""}
                      onChange={(e) => nutritionSet("vitamin_a_dv_percent", e.target.value)}
                    />
                  </Field>
                </div>
                <h3 className="recipe-subsection-title">Legacy nutrition fields</h3>
                <div className="recipe-form-grid four-columns">
                  <Field label="Legacy calories (kCal)">
                    <input
                      type="number"
                      min="0"
                      value={form.nutrition.kCal}
                      onChange={(e) => nutritionSet("kCal", e.target.value)}
                    />
                  </Field>
                  <Field label="Protein">
                    <input
                      value={form.nutrition.protein || ""}
                      onChange={(e) => nutritionSet("protein", e.target.value)}
                      placeholder="e.g. 14g"
                    />
                  </Field>
                  <Field label="Fat">
                    <input
                      value={form.nutrition.fat || ""}
                      onChange={(e) => nutritionSet("fat", e.target.value)}
                      placeholder="e.g. 9g"
                    />
                  </Field>
                  <Field label="Carbs">
                    <input
                      value={form.nutrition.carbs || ""}
                      onChange={(e) => nutritionSet("carbs", e.target.value)}
                      placeholder="e.g. 68g"
                    />
                  </Field>
                </div>
              </div>
              <div className="recipe-publish-grid">
                <div className="recipe-publish-settings">
                  <h3>Catalogue settings</h3>
                  <div className="recipe-form-grid">
                    <Field label="Display order">
                      <input
                        type="number"
                        value={form.index}
                        onChange={(e) => set("index", e.target.value)}
                      />
                    </Field>
                    <Field label="View count">
                      <input
                        type="number"
                        min="0"
                        value={form.view_count}
                        onChange={(e) => set("view_count", e.target.value)}
                      />
                    </Field>
                    <Field label="Rating">
                      <input
                        type="number"
                        min="0"
                        max="5"
                        step="0.01"
                        value={form.rating}
                        onChange={(e) => set("rating", e.target.value)}
                      />
                    </Field>
                    <Field label="Review count">
                      <input
                        type="number"
                        min="0"
                        value={form.review_count}
                        onChange={(e) => set("review_count", e.target.value)}
                      />
                    </Field>
                    <Field
                      label="Uploaded by user ID"
                      hint="Optional AppUser ID for attribution."
                    >
                      <input
                        type="number"
                        min="1"
                        value={form.user_id}
                        onChange={(e) => set("user_id", e.target.value)}
                      />
                    </Field>
                  </div>
                  <RecipeToggle
                    id="recipe-ai-generated"
                    className="recipe-toggle-row"
                    checked={form.is_ai_generated}
                    label="AI-generated recipe"
                    onChange={(checked) => set("is_ai_generated", checked)}
                  >
                    <span>
                      <strong>AI-generated recipe</strong>
                      <small>
                        Show this recipe as AI-assisted in internal reporting.
                      </small>
                    </span>
                  </RecipeToggle>
                </div>
                <div className="recipe-publish-card">
                  <span className="recipe-publish-card-icon">
                    <Sparkles size={22} />
                  </span>
                  <h3>Ready to publish?</h3>
                  <p>
                    Publishing makes this recipe available to the app. Saving a
                    draft keeps it hidden while preserving every field.
                  </p>
                  <div className="recipe-publish-actions">
                    <button
                      className="recipe-button recipe-button-secondary"
                      onClick={() => save(false)}
                    >
                      <Save size={17} /> Save draft
                    </button>
                    <button
                      className="recipe-button recipe-button-primary"
                      onClick={() => save(true)}
                    >
                      <Sparkles size={17} /> Publish recipe
                    </button>
                  </div>
                </div>
              </div>
            </section>
          )}

          <footer className="recipe-form-footer">
            <button
              className="recipe-button recipe-button-secondary"
              disabled={step === 0}
              onClick={() => setStep((value) => value - 1)}
            >
              Back
            </button>
            <span>
              Step {step + 1} of {STEPS.length}
            </span>
            {step < STEPS.length - 1 ? (
              <button
                className="recipe-button recipe-button-primary"
                onClick={() =>
                  validateStep(step) && setStep((value) => value + 1)
                }
              >
                Continue
              </button>
            ) : (
              <button
                className="recipe-button recipe-button-primary"
                onClick={() => save(true)}
              >
                <Sparkles size={17} /> Publish
              </button>
            )}
          </footer>
        </main>
      </div>

      {alerts.map((alert) => (
        <CustomAlert
          key={alert.id}
          {...alert}
          onClose={() => removeAlert(alert.id, setAlerts)}
        />
      ))}
    </div>
  );
};

const BookOpenIcon = () => <ChefHat size={24} />;

export default RecipeForm;
